import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import { Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { EscalationToggle } from "../escalation-toggle"
import {
  type BugReportFields,
  REPRODUCIBILITIES,
  type Reproducibility,
  SEVERITIES,
  type Severity,
  shouldEscalate,
  WHO_AFFECTED,
  type WhoAffected,
} from "../schemas"

type BugReportStepProps = {
  values: BugReportFields
  onChange: (values: BugReportFields) => void
  onBack: () => void
  onSubmit: () => void
  submitting: boolean
  evidenceSlot?: React.ReactNode
  /** Auto-captured screenshot UI, if available. Rendered below evidence. */
  screenshotSlot?: React.ReactNode
  /** User's consent to escalate, only meaningful while the report is eligible. */
  escalationRequested: boolean
  onEscalationRequestedChange: (requested: boolean) => void
}

export function BugReportStep({
  values,
  onChange,
  onBack,
  onSubmit,
  submitting,
  evidenceSlot,
  screenshotSlot,
  escalationRequested,
  onEscalationRequestedChange,
}: BugReportStepProps) {
  const { t } = useTranslation()

  const update = <K extends keyof BugReportFields>(key: K, value: BugReportFields[K]) => {
    onChange({ ...values, [key]: value })
  }

  const canContinue =
    values.whatTrying.trim().length > 0 &&
    values.expected.trim().length > 0 &&
    values.happened.trim().length > 0

  // Live eligibility — as soon as the user's answers tip the bug into
  // "something we'd want support eyes on", show the escalation toggle below
  // (default off) so they can decide whether to actually page support.
  // Pure calc off `values`.
  const escalation = useMemo(() => shouldEscalate("bug", values), [values])

  return (
    <div className="space-y-4">
      <TextField
        required
        label={t("feedback.bug.whatTryingLabel", "What were you trying to do?")}
        rows={2}
        value={values.whatTrying}
        placeholder={t(
          "feedback.bug.whatTryingPlaceholder",
          "e.g. Open the customer summary report for last quarter"
        )}
        onChange={(v) => update("whatTrying", v)}
      />

      <TextField
        required
        label={t("feedback.bug.expectedLabel", "What did you expect to happen?")}
        rows={2}
        value={values.expected}
        placeholder={t(
          "feedback.bug.expectedPlaceholder",
          "The report should open with the latest figures"
        )}
        onChange={(v) => update("expected", v)}
      />

      <TextField
        required
        label={t("feedback.bug.happenedLabel", "What happened instead?")}
        rows={3}
        value={values.happened}
        placeholder={t(
          "feedback.bug.happenedPlaceholder",
          "Include the exact error text if there is one"
        )}
        onChange={(v) => update("happened", v)}
      />

      <TextField
        monospace
        label={t("feedback.bug.errorTextLabel", "Error text (if any)")}
        placeholder={t("feedback.bug.errorTextPlaceholder", "Paste the exact error message here")}
        rows={2}
        value={values.errorText ?? ""}
        onChange={(v) => update("errorText", v)}
      />

      <RadioField<Reproducibility>
        label={t("feedback.bug.reproducibilityLabel", "Can you reproduce it?")}
        value={values.reproducibility}
        options={REPRODUCIBILITIES.map((v) => ({
          value: v,
          label: t(`feedback.bug.reproducibility.${v}`, defaultLabel(v)),
        }))}
        onChange={(v) => update("reproducibility", v)}
      />

      <div className="space-y-2">
        <RadioField<WhoAffected>
          label={t("feedback.bug.whoAffectedLabel", "Who is affected?")}
          value={values.whoAffected}
          options={WHO_AFFECTED.map((v) => ({
            value: v,
            label: t(`feedback.bug.whoAffected.${v}`, defaultLabel(v)),
          }))}
          onChange={(v) => update("whoAffected", v)}
        />
        {values.whoAffected === "other" && (
          <Input
            aria-label={t("feedback.bug.whoAffectedOtherLabel", "Describe who is affected")}
            className="focus-visible:border-primary focus-visible:ring-0"
            placeholder={t("feedback.bug.whoAffectedOtherPlaceholder", "Describe who is affected")}
            value={values.whoAffectedOther ?? ""}
            onChange={(e) => update("whoAffectedOther", e.target.value)}
          />
        )}
      </div>

      <RadioField<Severity>
        label={t("feedback.bug.severityLabel", "How severe is it?")}
        value={values.severity}
        options={SEVERITIES.map((v) => ({
          value: v,
          label: t(`feedback.bug.severity.${v}`, defaultLabel(v)),
        }))}
        onChange={(v) => update("severity", v)}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="bug-when">
            {t("feedback.bug.whenStartedLabel", "When did it happen?")}
          </label>
          <Input
            className="focus-visible:border-primary focus-visible:ring-0"
            id="bug-when"
            type="datetime-local"
            value={values.whenStarted ?? ""}
            onChange={(e) => update("whenStarted", e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="bug-page-feature">
            {t("feedback.bug.pageFeatureLabel", "Where in the app?")}
          </label>
          <Input
            className="focus-visible:border-primary focus-visible:ring-0"
            id="bug-page-feature"
            value={values.pageFeature ?? ""}
            placeholder={t(
              "feedback.bug.pageFeaturePlaceholder",
              "e.g. Explorer chat, Tender review"
            )}
            onChange={(e) => update("pageFeature", e.target.value)}
          />
        </div>
      </div>

      {evidenceSlot}
      {screenshotSlot}

      {escalation.escalate && (
        <EscalationToggle
          requested={escalationRequested}
          checkboxLabel={t(
            "feedback.bug.escalationCheckbox",
            "Notify support now — expect a reply within 1 business day"
          )}
          description={t(
            "feedback.bug.escalationBanner",
            "Looks serious. Check the box below if you want this routed straight to support instead of our regular backlog."
          )}
          onToggle={onEscalationRequestedChange}
        />
      )}

      <div className="flex justify-between gap-2 pt-2">
        <Button disabled={submitting} variant="outline" onClick={onBack}>
          {t("common.back", "Back")}
        </Button>
        <Button disabled={submitting || !canContinue} onClick={onSubmit}>
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {t("common.sending", "Sending...")}
            </>
          ) : (
            t("feedback.actions.review", "Review")
          )}
        </Button>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Local field helpers
// -----------------------------------------------------------------------------

type TextFieldProps = {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  required?: boolean
  monospace?: boolean
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  rows = 2,
  required,
  monospace,
}: TextFieldProps) {
  const id = `feedback-bug-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium" htmlFor={id}>
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </label>
      <Textarea
        id={id}
        placeholder={placeholder}
        rows={rows}
        value={value}
        className={cn(
          "focus-visible:border-primary focus-visible:ring-0",
          monospace && "font-mono text-xs"
        )}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

type RadioFieldProps<T extends string> = {
  label: string
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
}

function RadioField<T extends string>({ label, value, onChange, options }: RadioFieldProps<T>) {
  // Plain toggle-buttons rather than `RadioGroup` + dot — the filled indicator
  // was visually off-centre in a few cases, and the card border/background
  // already communicates the selection clearly.
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup">
        {options.map((opt) => {
          const active = value === opt.value
          return (
            <button
              key={opt.value}
              aria-checked={active}
              role="radio"
              type="button"
              className={cn(
                "flex items-center justify-center rounded-md border px-3 py-2 text-center text-xs transition-colors",
                active
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground bg-background hover:bg-accent/40"
              )}
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function defaultLabel(key: string): string {
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}
